import React from 'react';
import { connect } from 'react-redux';
import { setMonth, setYear, format } from 'date-fns';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { withStyles } from '@mui/styles';

import { LoadingView } from './LoadingView';
import { TitleTooltip } from './TitleTooltip';

import {
  StoreUtil,
} from 'doctivity-shared/utils';

import {
  loadAnalytics,
} from 'store/actions/analyticsActions';

// The two affiliation charts on the Visits page. `side` picks which provider on the
// visit is judged affiliated or not: the one who made the referral, or the one who
// received it. Each has its own report type, so both can be on screen at once.
const SIDES = {
  referrer: {
    type: 'REFERRING_AFFILIATION_BY_MONTH',
    title: 'Referring Provider Affiliation over Time',
    axis: 'Visits by Referring Provider',
    info: 'Visits each month, split by the provider who made the referral: affiliated '
      + 'with your organization, not affiliated, or no referring provider (None). The '
      + 'line is the total visits for the month. When a label is selected, only visits '
      + 'referred by providers in that label are counted, so None will be empty.',
  },
  provider: {
    type: 'REFERRED_AFFILIATION_BY_MONTH',
    title: 'Referred Provider Affiliation over Time',
    axis: 'Visits by Referred Provider',
    info: 'Visits each month, split by the provider who received the referral: '
      + 'affiliated with your organization, not affiliated, or no referred provider on '
      + 'the visit (None). The line is the total visits for the month. When a label is '
      + 'selected, only visits referred by providers in that label are counted.',
  },
};

const styles = (theme) => ({
  chartWrapper: {
    '& .apexcharts-legend': {
      display: 'flex !important',
      flexDirection: 'row !important',
      flexWrap: 'nowrap !important',
      justifyContent: 'center !important',
      alignItems: 'center !important',
      gap: '0 !important',
    },
    '& .apexcharts-legend-group': {
      display: 'inline-flex !important',
      flexDirection: 'row !important',
      margin: '0 !important',
      padding: '0 !important',
    },
    '& .apexcharts-legend-series': {
      display: 'inline-flex !important',
      margin: '0 6px !important',
      whiteSpace: 'nowrap !important',
    },
    '& .apexcharts-legend-text': {
      fontSize: '12px !important',
    },
  },
  card: {
    marginTop: theme.spacing(3),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  nothingFound: {
    fontWeight: 300,
    fontSize: 12,
  },
});

class ReferringAffiliationOverTimeChart extends React.Component {
  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.clientId !== prevProps.clientId
      || this.props.app.dateRange !== prevProps.app.dateRange
      || this.props.department !== prevProps.department
      || this.props.referringOrganization !== prevProps.referringOrganization
      || this.props.labelId !== prevProps.labelId
      || this.props.side !== prevProps.side) {
      this.fetchData();
    }
  }

  fetchData() {
    this.props.dispatch(loadAnalytics({
      type: SIDES[this.props.side].type,
      filter: {
        client_id: this.props.clientId,
        department: this.props.department,
        referring_organization: this.props.referringOrganization,
        tag_id: this.props.labelId,
        from: this.props.app.dateRange.start,
        to: this.props.app.dateRange.end,
      },
    }));
  }

  render() {
    const {
      classes,
      data,
      side,
    } = this.props;

    let graph;
    if (!data || StoreUtil.isLoading(data)) {
      graph = (<LoadingView />);
    } else if (data.data && data.data.length > 0) {
      const rows = StoreUtil.getData(data);
      const labels = {
        formatter: (value) => {
          return value.toLocaleString();
        },
      };
      graph = (
        <div className={classes.chartWrapper}>
          <Chart
            type='line'
            height={260}
            options={{
              chart: {
                id: `${side}_affiliation_over_time`,
                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
              },
              xaxis: {
                categories: rows.map((row) => `${format(setMonth(new Date(), row.month), 'MMM')} ${format(setYear(new Date(), row.year), 'yy')}`),
              },
              tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
              },
              stroke: {
                curve: 'smooth',
                width: [0, 0, 0, 3],
              },
              dataLabels: {
                enabled: false,
              },
              legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                itemMargin: {
                  horizontal: 10,
                  vertical: 0,
                },
                markers: {
                  width: 12,
                  height: 12,
                },
              },
              yaxis: [
                {
                  seriesName: 'Affiliated',
                  axisTicks: {
                    show: true,
                  },
                  title: {
                    text: SIDES[side].axis,
                  },
                  labels,
                },
                {
                  seriesName: 'Affiliated',
                  show: false,
                  labels,
                },
                {
                  seriesName: 'Affiliated',
                  show: false,
                  labels,
                },
                {
                  seriesName: 'Total Visits',
                  opposite: true,
                  axisTicks: {
                    show: true,
                  },
                  title: {
                    text: 'Total Visits',
                  },
                  labels,
                },
              ],
            }}
            series={[
              {
                name: 'Affiliated',
                data: rows.map((row) => row.affiliated),
                type: 'column',
              },
              {
                name: 'Non-Affiliated',
                data: rows.map((row) => row.non_affiliated),
                type: 'column',
              },
              {
                name: '(None)',
                data: rows.map((row) => row.none),
                type: 'column',
              },
              {
                name: 'Total Visits',
                data: rows.map((row) => row.total),
                type: 'line',
              },
            ]}
          />
        </div>
      );
    } else {
      graph = (
        <Typography className={classes.nothingFound}>
          No referring affiliation data available.
        </Typography>
      );
    }

    return (
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <TitleTooltip
              title={SIDES[side].title}
              tooltip={SIDES[side].info}
            />
          </div>

          {graph}

        </CardContent>
      </Card>
    );
  }
}

ReferringAffiliationOverTimeChart.propTypes = {
  dispatch: PropTypes.func.isRequired,
  classes: PropTypes.object,
  app: PropTypes.object,
  data: PropTypes.object,
  clientId: PropTypes.number.isRequired,
  department: PropTypes.string,
  referringOrganization: PropTypes.string,
  labelId: PropTypes.number,
  side: PropTypes.oneOf(Object.keys(SIDES)).isRequired,
};

function mapStateToProps(state, ownProps) {
  return {
    app: state.app,
    data: StoreUtil.get(state.analytics, SIDES[ownProps.side].type),
  };
}

const styled = withStyles(styles)(ReferringAffiliationOverTimeChart);
const connected = connect(mapStateToProps)(styled);
export { connected as ReferringAffiliationOverTimeChart };
